import { Body, Button } from '@assembly-js/design-system';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TextField } from 'src/components/TextField';
import { AUTH_STATES } from 'src/constants/authConsts';
import { GoogleAuthButton } from 'src/legacy/components/UI/Buttons/GoogleAuthButton';
import { isIdentityProviderDomain } from 'src/utils/AuthUtils';

import { StudioAuthLogo } from './StudioAuthLogo';
import { StudioPageFrame } from './StudioPageFrame';
import {
  STUDIO_BUTTON_FOCUS_CLASS,
  STUDIO_INPUT_CLASS,
  STUDIO_PRIMARY_BUTTON_CLASS,
} from './studioStyles';

export interface StudioLoginCredentialsFormValues {
  email: string;
  password: string;
}

export interface StudioLoginCredentialsFormProps {
  initialEmail?: string;
  initialPassword?: string;
  onContinueWithGoogle: () => void;
  onSubmitCredentials: (
    values: StudioLoginCredentialsFormValues,
  ) => Promise<void>;
  footerPrompt: string;
  footerActionLabel: string;
  footerActionHref: string;
}

const buildForgotPasswordHref = (): string => {
  const params = new URLSearchParams(window.location.search);
  params.set('step', AUTH_STATES.FORGOT_PASSWORD);
  return `?${params.toString()}`;
};

export const StudioLoginCredentialsForm = ({
  initialEmail = '',
  initialPassword = '',
  onContinueWithGoogle,
  onSubmitCredentials,
  footerPrompt,
  footerActionLabel,
  footerActionHref,
}: StudioLoginCredentialsFormProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email(t(($) => $.page_onboarding.studio.email_invalid))
          .required(t(($) => $.page_onboarding.studio.email_required)),
        password: Yup.string(),
      }),
    [t],
  );

  return (
    <StudioPageFrame>
      <div className="mx-auto flex w-full min-w-0 max-w-full flex-col items-stretch gap-8 sm:gap-10">
        <div className="flex w-full flex-col items-center gap-4">
          <StudioAuthLogo />
          <Body className="w-full text-center font-studio text-2xl font-normal leading-[1.25] text-black sm:text-[30px]">
            {t(($) => $.page_onboarding.studio.login.title)}
          </Body>
          <Body
            size="lg"
            className="mb-0 hidden w-full font-studio text-center text-[#5f636a] sm:block"
          >
            {t(($) => $.page_onboarding.studio.login.subtitle)}
          </Body>
        </div>

        <Formik<StudioLoginCredentialsFormValues>
          initialValues={{ email: initialEmail, password: initialPassword }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setFieldError, setSubmitting }) => {
            const trimmedEmail = values.email.trim();
            const trimmedPassword = values.password.trim();

            try {
              await onSubmitCredentials({
                email: trimmedEmail,
                password: trimmedPassword,
              });
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : t(($) => $.page_onboarding.studio.login.sign_in_failed);
              if (trimmedPassword) {
                setFieldError('password', message);
              } else {
                setFieldError('email', message);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => {
            const hidePassword = isIdentityProviderDomain(values.email);
            const usesPassword = Boolean(values.password.trim());
            const submitLabel =
              hidePassword || usesPassword
                ? t(($) => $.page_onboarding.studio.login.sign_in_with_password)
                : t(($) => $.page_onboarding.studio.login.continue_with_email);

            return (
              <Form className="flex w-full min-w-0 self-stretch flex-col gap-5 sm:gap-6">
                <GoogleAuthButton
                  onClick={onContinueWithGoogle}
                  className={clsx(
                    '!w-full h-12 hover:!bg-[#f5f5f0] [&_.MuiTypography-root]:!text-[15px] [&_.MuiTypography-root]:!font-normal',
                    STUDIO_BUTTON_FOCUS_CLASS,
                  )}
                />

                <div className="flex w-full items-center gap-5">
                  <div className="h-px flex-1 bg-non-hover-border" />
                  <span className="font-studio text-[12px] leading-[1.5] text-[#9aa0a6] sm:text-[13px] sm:text-[#5f636a]">
                    {t(($) => $.page_onboarding.studio.or)}
                  </span>
                  <div className="h-px flex-1 bg-non-hover-border" />
                </div>

                <div className="flex w-full flex-col gap-1.5 sm:gap-2">
                  <label
                    htmlFor="studio-login-email"
                    className="font-studio text-[13px] font-normal leading-[1.5] text-black"
                  >
                    {t(($) => $.page_onboarding.studio.email_label)}
                  </label>
                  <TextField
                    id="studio-login-email"
                    sizeVariant="tall"
                    fullWidth
                    type="email"
                    name="email"
                    variant="outlined"
                    className={STUDIO_INPUT_CLASS}
                    placeholder={t(
                      ($) => $.page_onboarding.studio.email_placeholder,
                    )}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                    error={Boolean(touched.email && errors.email)}
                    helperText={
                      touched.email && errors.email ? errors.email : undefined
                    }
                  />
                </div>

                {!hidePassword ? (
                  <div className="flex w-full flex-col gap-2">
                    <label
                      htmlFor="studio-login-password"
                      className="font-studio text-[13px] leading-[1.5] text-black"
                    >
                      {t(($) => $.page_onboarding.studio.login.password_label)}
                    </label>
                    <TextField
                      id="studio-login-password"
                      sizeVariant="tall"
                      fullWidth
                      type="password"
                      name="password"
                      variant="outlined"
                      className={STUDIO_INPUT_CLASS}
                      placeholder={t(
                        ($) =>
                          $.page_onboarding.studio.login.password_placeholder,
                      )}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="current-password"
                      error={Boolean(touched.password && errors.password)}
                      helperText={
                        touched.password && errors.password
                          ? errors.password
                          : undefined
                      }
                    />
                    <a
                      href={buildForgotPasswordHref()}
                      className="self-start font-studio text-[13px] leading-[1.5] text-[#5f636a] underline-offset-2 hover:underline"
                    >
                      {t(($) => $.page_onboarding.studio.login.forgot_password)}
                    </a>
                  </div>
                ) : null}

                <Button
                  type="submit"
                  id="studioLoginSubmit"
                  className={clsx('h-12 w-full', STUDIO_PRIMARY_BUTTON_CLASS)}
                  variant="studio"
                  label={submitLabel}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />

                <p className="m-0 text-center font-studio text-[13px] leading-[1.5] text-[#5f636a]">
                  {footerPrompt}{' '}
                  <a
                    href={footerActionHref}
                    className="text-black underline-offset-2 hover:underline"
                  >
                    {footerActionLabel}
                  </a>
                </p>
              </Form>
            );
          }}
        </Formik>
      </div>
    </StudioPageFrame>
  );
};
